import type { BoardItem, CellValue, CellRef } from '@/pages/Boards/hooks'

export type QueueSortKey = 'default' | 'dueDate'
export type QueueSortDirection = 'asc' | 'desc'
export type QueueSort = { key: QueueSortKey; direction: QueueSortDirection }

export type QueueAiFilter = 'all' | 'ai' | 'human'

export type QueueFilters = {
  status: string | null
  priorities: string[]
  assignees: string[]
  aiGenerated: QueueAiFilter
  // Inclusive due-date window, as 'YYYY-MM-DD'. Either end can stand alone
  // (open-ended before/after). Both null = no due-date filter.
  dueFrom: string | null
  dueTo: string | null
}

// Column ids resolved BY TYPE from the board (never assume id === type).
export type QueueColumnIds = {
  status?: string
  priority?: string
  person?: string
  date?: string
}

// Sentinel assignee-filter values. A real value is a person reference string.
export const ME_ASSIGNEE = 'me'
export const UNASSIGNED_ASSIGNEE = 'unassigned'

export const DEFAULT_QUEUE_FILTERS: QueueFilters = {
  status: null,
  priorities: [],
  assignees: [],
  aiGenerated: 'all',
  dueFrom: null,
  dueTo: null,
}

export const DEFAULT_QUEUE_SORT: QueueSort = { key: 'default', direction: 'asc' }

const asString = (value: CellValue | undefined): string | null =>
  typeof value === 'string' ? value : null

const asRef = (value: CellValue | undefined): CellRef | null =>
  value && typeof value === 'object' ? value : null

/** A task is auto-generated if it came from a progress note OR a lab result. */
export const isAiGenerated = (item: {
  sourceEncounterId?: string
  sourceDiagnosticReportId?: string
}): boolean => item.sourceEncounterId != null || item.sourceDiagnosticReportId != null

// Filter a list by every active dimension (AND across dimensions; OR within a
// multi-select). A dimension whose column is missing, or whose selection is
// empty, is inactive and matches everything.
export function filterQueueItems(
  items: BoardItem[],
  filters: QueueFilters,
  cols: QueueColumnIds,
  myReference: string | undefined,
): BoardItem[] {
  return items.filter((item) => {
    if (filters.status && cols.status) {
      if (asString(item.values[cols.status]) !== filters.status) return false
    }
    if (filters.priorities.length && cols.priority) {
      const value = asString(item.values[cols.priority])
      if (!value || !filters.priorities.includes(value)) return false
    }
    if (filters.assignees.length && cols.person) {
      const ref = asRef(item.values[cols.person])?.reference ?? null
      const matches = filters.assignees.some((a) => {
        if (a === UNASSIGNED_ASSIGNEE) return ref === null
        if (a === ME_ASSIGNEE) return !!myReference && ref === myReference
        return ref === a
      })
      if (!matches) return false
    }
    if ((filters.dueFrom || filters.dueTo) && cols.date) {
      // Stored due dates are plain 'YYYY-MM-DD', so they compare lexically (same
      // assumption sortQueueItems makes). A task with no due date falls outside
      // any window.
      const due = asString(item.values[cols.date])
      if (!due) return false
      if (filters.dueFrom && due < filters.dueFrom) return false
      if (filters.dueTo && due > filters.dueTo) return false
    }
    if (filters.aiGenerated === 'ai' && !isAiGenerated(item)) return false
    if (filters.aiGenerated === 'human' && isAiGenerated(item)) return false
    return true
  })
}

// Sort by due date; items with no due date always sink to the bottom, both
// directions. Default sort returns the list unchanged (server createdAt order).
// Array.sort is stable, so equal dates keep their incoming order.
export function sortQueueItems(
  items: BoardItem[],
  sort: QueueSort,
  dateColumnId: string | undefined,
): BoardItem[] {
  if (sort.key !== 'dueDate' || !dateColumnId) return items
  const dir = sort.direction === 'asc' ? 1 : -1
  return [...items].sort((a, b) => {
    const da = asString(a.values[dateColumnId])
    const db = asString(b.values[dateColumnId])
    if (!da && !db) return 0
    if (!da) return 1
    if (!db) return -1
    return da < db ? -dir : da > db ? dir : 0
  })
}

export function applyQueueFiltersAndSort(
  items: BoardItem[],
  filters: QueueFilters,
  sort: QueueSort,
  cols: QueueColumnIds,
  myReference: string | undefined,
): BoardItem[] {
  return sortQueueItems(filterQueueItems(items, filters, cols, myReference), sort, cols.date)
}

// Which column-backed filters the current board actually supports. A dimension
// whose column is missing can't filter anything, so it doesn't count as active
// (and the panel hides its control). Omitted keys default to available.
export type QueueFilterAvailability = {
  status?: boolean
  priority?: boolean
  assignee?: boolean
  dueDate?: boolean
}

// Number of active FILTER dimensions (Sort is excluded — it isn't a filter).
// A column-backed dimension only counts when its column is present on the board,
// so the badge never claims a filter is active while it silently does nothing.
export function activeFilterCount(
  filters: QueueFilters,
  available: QueueFilterAvailability = {},
): number {
  let count = 0
  if (filters.status && available.status !== false) count++
  if (filters.priorities.length && available.priority !== false) count++
  if (filters.assignees.length && available.assignee !== false) count++
  // The whole window is one dimension, however many ends are set.
  if ((filters.dueFrom || filters.dueTo) && available.dueDate !== false) count++
  if (filters.aiGenerated !== 'all') count++
  return count
}

// Newest note/reply timestamp on a task (ISO strings sort lexically), or null
// when it has no updates yet.
export const latestNoteTime = (item: BoardItem): string | null => {
  let latest: string | null = null
  for (const note of item.notes) {
    if (!latest || note.createdAt > latest) latest = note.createdAt
    for (const reply of note.replies) if (reply.createdAt > latest) latest = reply.createdAt
  }
  return latest
}
